import React, { useState, useEffect } from "react";
import DataTable from 'react-data-table-component';
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ItemsService } from "../../../services/user/items.services";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";

const ItemsComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [itemsData, setItemsData] = useState([]);
    const [filterText, setFilterText] = useState("");
    const pageNumber = location?.state?.pageNumber || 1;
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const [showModal, setShowModal] = useState(false);
    const [itemsToDelete, setItemsToDelete] = useState(null);
    const rowsPerPage = 5;

    useEffect(() => {
        const fetchItemsData = async () => {
            try {
                const response = await ItemsService.getAllItems(selectedStore);
                if (Array.isArray(response)) {
                    setItemsData(response);
                }
                //setItemsData(response);
            } catch (error) {
                console.error("Error fetching Items Data", error);
                setItemsData([]);
            }
        };
        fetchItemsData();
    }, []);

    // Helper to convert image buffer to image URL (Blob)
    const getImageFromBuffer = (buffer) => {
        if (buffer?.data) {
            const blob = new Blob([new Uint8Array(buffer.data)], { type: 'image/jpeg' });
            return URL.createObjectURL(blob);
        }
        return '';
    };

    const columns = [
        {
            name: "Image",
            selector: row => row?.itemImage ? getImageFromBuffer(row.itemImage) : "",
            cell: row => (
                <img src={getImageFromBuffer(row?.itemImage)} alt={row?.itemName} style={{ width: "50px", height: "50px", objectFit: "cover" }} />
            ),
        },
        {
            name: "Item Name",
            selector: row => row.itemName,
            width: "15%",
        },
        {
            name: "Category",
            selector: row => row.category?.categoryName,
            sortable: true,
            width: "15%",
        },
        {
            name: "Item Type",
            selector: row => row.itemtype,
            width: "15%",
        },
        {
            name: "Products",
            selector: row => row.childType,
        }, {
            name: "Products Type",
            selector: row => row.subchild,
            width: "15%",
        },
        {
            name: "Price",
            width: "10%",
            selector: row => row.price,
            cell: row => <div><i className="fadeIn animated bx bx-rupee" />{row.price}</div>
        },
        {
            name: "Active?",
            selector: row => row?.isActive,
            cell: row => <button className={`btn btn-light radius-30 px-4 ${row.isActive ? 'text-success' : 'text-danger'}`}>
                {row.isActive === true ? "Active" : "Inactive"}
            </button>,
            center: true,
        },
        {
            name: "Action",
            button: true,
            width: "15%",
            cell: row => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>Delete</button>
                </div>
            ),
        },
    ];

    // Filter data based on search text
    const filteredData = itemsData.filter(item =>
        Object.values(item).some(value => value?.toString().toLowerCase().includes(filterText.toLowerCase()))
    );

    const handleSearchChange = (event) => {
        setFilterText(event.target.value)
    };

    // Slice the filtered data for the current page
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    // Calculate total pages
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // Handle the page change
    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleEditClick = (row) => {
        try {
            navigate('/items/edit', { state: { itemId: row?.itemId } });
        } catch (error) {
            console.error('Error navigating to edit page:', error);
        }
    };

    const handleDeleteClick = (item) => {
        setItemsToDelete(item);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (itemsToDelete) {
                await ItemsService.deleteItemById(itemsToDelete.itemId);
                setItemsData(prevData => prevData.filter(item => item.itemId !== itemsToDelete.itemId));
                handleNotify("Item deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
            }
            setShowModal(false);
        } catch (error) {
            handleNotify(error || 'Error while deleting Item.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
    };
    const handleAddNewClick = () => {
        navigate('/items/create', { state: { pageNumber: currentPage } });
    };

    return (
        <div className="page-wrapper m-0 p-4">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">Items</div>
                    <div className="ms-auto">
                        <button className="btn btn-primary" onClick={handleAddNewClick}>New +</button>
                    </div>
                </div>
                <div className="card p-4 radius-10 border-top border-0 border-4 border-primary">
                    <div className="card-body">
                        <div className="d-flex justify-content-between mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>
                            <div className="d-flex align-items-center">
                                <span className="fw-bold me-2">Search:</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search..."
                                    value={filterText}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        </div>

                        {currentData && (
                            <div className="table-responsive">
                                <DataTable
                                    columns={columns}
                                    data={currentData}
                                    fixedHeader
                                    responsive
                                    highlightOnHover
                                    sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                />
                            </div>
                        )}

                        <div className="d-flex mt-4 justify-content-end">
                            <div className="btn-group">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                >
                                    Prev
                                </button>
                                {Array.from({ length: totalPages }, (_, index) => (
                                    <button
                                        key={index}
                                        className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showModal &&
                <DeleteModelPopupComponent
                    show={showModal}
                    onClose={handleModalClose}
                    onConfirm={handleDeleteConfirm}
                    title="Confirm"
                    confirmText="Delete"
                    Message="Are you sure want to delete selected record(s)?"
                />
            }
        </div>
    );
};

export default ItemsComponent;
